/**
 * Registers `public/sw.js` (app shell cache, v2.1 B14/D06) in production builds only.
 * In dev the Vite server owns every request, so no worker is installed there.
 *
 * A few seconds after the page has loaded, the worker is asked to precache the rest of this
 * release's chunks (ADR 0042): later page changes need no download, and a tab that stays open
 * across a deploy keeps finding the chunks of its own release. It runs when the browser is idle,
 * never on a data-saver connection, and only fetches what is not cached yet.
 *
 * Right away, the worker is also told which of the release's files this page has already loaded
 * (`xc-warm`) — the page's own chunk is not in index.html, and without this the second visit of the
 * page would still download it when the first visit ended before the precache ran.
 */
const PRECACHE_DELAY_MS = 6000;

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch(() => {
      /* offline shell is a convenience; the app works without it */
    });
    void navigator.serviceWorker.ready
      .then((reg) => {
        const loaded = performance.getEntriesByType('resource').map((e) => e.name).filter((u) => u.startsWith(`${location.origin}/assets/`));
        if (loaded.length) reg.active?.postMessage({ type: 'xc-warm', urls: loaded });
      })
      .catch(() => undefined);
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (saveData) return;
    const ask = () => {
      void navigator.serviceWorker.ready.then((reg) => reg.active?.postMessage({ type: 'xc-precache' })).catch(() => undefined);
    };
    setTimeout(() => {
      const idle = (window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
      if (idle) idle(ask, { timeout: 10_000 });
      else ask();
    }, PRECACHE_DELAY_MS);
  });
}
